import {
  Button,
  Col,
  Empty,
  Flex,
  Form,
  Input,
  notification,
  Row,
  Space,
  Tooltip,
  Typography,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { __rest } from "../../../../utils";
import { FaSave } from "react-icons/fa";
import { createOrUpdateNode } from "../../../../services";
import { useEffect } from "react";
import Utils from "../../../../utils/functions";
import { useLocation, useNavigate } from "react-router-dom";

const { Paragraph } = Typography;

export const NodeType = {
  SECTION: "section",
  SUB_SECTION: "subsection",
  TABLE: "table",
  QUIZ: "quiz",
  CASE: "case",
};

export const NodeParagraphForm = ({
  node,
  onCancel = () => null,
  onDeleteParagraph = () => null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [notificationApi, notificationCtxHolder] =
    notification.useNotification();

  const handleSubmitFields = () => {
    form
      .validateFields()
      .then(({ nodeParagraphs }) => {
        const prev = nodeParagraphs?.nodeParagraphs || [];
        const payload = {
          ...node, // prev props
          conditionUid: node?.condition?.uid,
          nodeParagraphs: [...prev, ...nodeParagraphs],
        };

        createOrUpdateNode({ node: payload })
          .then((response) => {
            setTimeout(() => {
              notificationApi.open({
                key: "node-update-success-notify",
                type: "success",
                message: `${Utils.firstLetterToUpperCase(
                  "payload"?.title?.trim()
                )} updated`,
                description: `Request successful`,
                showProgress: true,
                pauseOnHover: true,
              });
              form.resetFields();
              onCancel();
              navigate(
                `/manage/cpd/conditions/${location?.state?.condition?.uid}/details`,
                {
                  state: {
                    condition: location?.state?.condition,
                    elements: location?.state?.elements,
                  },
                }
              );
            }, 50);
          })
          .catch((error) => {
            console.log(error);
            setTimeout(() => {
              notificationApi.open({
                key: "node-update-error-notify",
                type: "error",
                message: "Request update Failed",
                description: `${error}`,
                showProgress: true,
                pauseOnHover: true,
              });
            }, 50);
          });
      })
      .catch((error) => {
        console.log(error);
        setTimeout(() => {
          notificationApi.open({
            key: "node-validation-error-notify",
            type: "error",
            message: "Form validation failed",
            description: `${error}`,
            showProgress: true,
            pauseOnHover: true,
          });
        }, 50);
      });
  };

  useEffect(() => {
    form.setFieldsValue({ nodeParagraphs: node?.nodeParagraphs || [] });
  }, [node, form]);

  return (
    <Form form={form}>
      {notificationCtxHolder}
      <Form.List name="nodeParagraphs" label="Summary">
        {(fields, { add, remove }, { errors }) => {
          return (
            <>
              {fields.map((field, index) => {
                const restField = __rest(field, ["uid", "content"]);
                return (
                  <Space.Compact
                    key={restField.fieldKey}
                    style={{ display: "flex", marginBottom: 2 }}
                    align="baseline"
                  >
                    <Form.Item
                      {...restField}
                      name={[field.name, "content"]}
                      validateDebounce={2000}
                      rules={[
                        {
                          required: true,
                          message: "Missing paragraph text",
                        },
                      ]}
                      getValueFromEvent={(evt) =>
                        typeof evt === "object" && evt?.target
                          ? evt.target.value
                          : evt
                      }
                    >
                      <Input.TextArea
                        placeholder="Paragraph text"
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        style={{
                          minWidth: "50vw",
                          minHeight: "5rem",
                          flex: 1,
                        }}
                      />
                    </Form.Item>
                    <Tooltip
                      title="Click to remove this paragraph spec."
                      placement="topRight"
                      arrow={false}
                      color="red"
                      key={`tooltip-remove-paragraph-${field.name}-${index}`}
                    >
                      <Button
                        danger
                        onClick={() => remove(field.name)}
                        style={{ minHeight: "3.3rem" }}
                      >
                        <MinusCircleOutlined />
                      </Button>
                    </Tooltip>
                  </Space.Compact>
                );
              })}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  style={{ minWidth: "30vw" }}
                  icon={<PlusOutlined />}
                >
                  Add paragraph
                </Button>
                <Form.ErrorList errors={errors} />
              </Form.Item>
            </>
          );
        }}
      </Form.List>

      <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
        <Col span={24}>
          <Form.Item label="">
            <Flex gap="small">
              <Button
                type="dashed"
                icon={<FaSave />}
                onClick={() => handleSubmitFields()}
              >
                Save changes{" "}
              </Button>
              <Button danger onClick={onCancel}>
                Cancel
              </Button>
            </Flex>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export const EmptyDataPane = ({ textMessage, children }) => {
  return (
    <Empty
      image={"https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"}
      styles={{ image: { height: 100 } }}
      description={textMessage}
    >
      {children}
    </Empty>
  );
};

export const renderNodeParagraphType = ({ node, onCreateNew }) => {
  if (node === null) return null;

  if (node?.nodeParagraphs?.length === 0 && node?.type !== NodeType.QUIZ) {
    return (
      <EmptyDataPane
        textMessage={`${node?.title} does not have any summary text`}
      >
        <Button
          type="dashed"
          icon={<PlusOutlined style={{ margin: "5%" }} />}
          onClick={onCreateNew}
        >
          Add paragraph
        </Button>
      </EmptyDataPane>
    );
  }

  return node?.nodeParagraphs?.map((para, index) => {
    return (
      <Paragraph
        key={index}
        style={{ fontSize: 18, lineHeight: 1.4, textWrap: "wrap" }}
      >
        <div dangerouslySetInnerHTML={{ __html: para?.content }}></div>
      </Paragraph>
    );
  });
};

export const renderSectionNodeType = ({ node }) => {
  if (node === null) return null;

  return (
    <Paragraph
      key={node?.title}
      style={{ fontSize: 18, lineHeight: 1.4, textWrap: "wrap" }}
    >
      {node?.title}
    </Paragraph>
  );
};

export const getQuizForCondition = (nodes) => {
  const nodeID = nodes ? nodes?.filter((node) => node.type === NodeType?.QUIZ)[0]
    : null;
  return nodeID;
};

export const getNodePageListTitles = (nodes) => {

  return nodes
    ? nodes
        ?.filter((node) => node?.type === NodeType?.SECTION)
        .sort((a, b) => a?.title?.localeCompare(b?.title))
        .map((section) => ({
          label: section.title.trim(),
          icon: null,
          key: section?.uid,
        }))
    : [];
};

export const filterNodesByTypeAndParent = (currNode, nodes = []) => {
  return nodes?.filter(
    (node) =>
      node?.type === NodeType.TABLE && node?.parent?.uid === currNode?.uid
  )[0];
};

export const filterQuizNoodes = (nodes = []) => {
  return nodes?.filter(
    (node) =>
      node?.type === NodeType.QUIZ
  )[0];
};

export const buildNodeHierarchy = (nodes) => {
  const nodeMap = new Map();
  const roots = [];

  // First, map all nodes by their uid
  nodes.forEach((node) => {
    node.children = []; // prepare to hold child nodes
    nodeMap.set(node.uid, node);
  });

  // Second, link children to their parents
  nodes.forEach((node) => {
    const parentUid = node.parent?.uid;
    if (parentUid && nodeMap.has(parentUid)) {
      const parentNode = nodeMap.get(parentUid);
      parentNode.children.push(node);
    } else {
      // If no parent found, it's a root node
      roots.push(node);
    }
  });

  return roots;
};
